import { useId, useMemo, useState } from 'react';
import { ArrowLeft, ArrowRight, ChevronDown, X } from 'lucide-react';
import { getTeamPlayerIds } from '../../engine';
import { EloChart } from '../diagrams/EloChart';
import { PlayerMatchRow } from './PlayerMatchRow';
import { MeritsView } from './MeritsView';
import { AllMatchesView } from './AllMatchesView';
import { useLocale } from '../../i18n';
import { playerDisplayName } from '../../lib/playerName';
import { Dialog } from '../ui';
import { safeId } from '../ui/labelledControl';

export interface PlayerHistoryModalProps {
  /** The player whose full cross-tournament profile is shown. */
  playerId: any;
  /** The effective player DB (tenant-scoped) used to resolve the player record. */
  effectivePlayerDB: any[];
  /** Archive of completed tournaments, the source of the player's history. */
  archive: any[];
  /** Single (non-tournament) matches the player took part in. */
  singleMatches?: any[];
  /** Manual ELO adjustments for the player. */
  eloAdjustments?: any[];
  /** Closes the modal. */
  onClose: () => void;
  /** Opens an archived tournament from the history/merits list. */
  onOpenArchived: (tournament: any) => void;
  /**
   * The live `Padel24Engine` wiring object, passed in as a prop. Read
   * for runtime-mutable ELO settings (`.elo.*`, `.settings.NEWBIE_K_MULTIPLIER`) and
   * the per-format `.getTournamentType` resolver — kept verbatim off the prop.
   */
  Padel24Engine: any;
  /**
   * Resolves the player's per-tournament history rows. deviation: this is the
   * `getPlayerTournamentHistory` helper, which is not yet extracted to src/engine
   * (it calls other helpers that still live outside src/engine); it is threaded
   * in as a prop and the call-site forwards its own copy.
   */
  getPlayerTournamentHistory: (playerId: any, archive: any[], playerName?: any) => any[];
  /**
   * Resolves a player's final placement in a tournament. deviation: same as
   * `getPlayerTournamentHistory` above — a helper not yet extracted to src/engine,
   * passed as a prop.
   */
  calcPlayerPositionInTournament: (playerId: any, tournament: any) => any;
}

/**
 * Player full-history modal — header (status badges, ELO summary), stats grid,
 * an ELO-development chart, and a three-way toggle between all matches, per-tournament
 * history and merits. Extracted verbatim from the original monolith (legacy/App.jsx);
 * markup/logic unchanged. Takes the live `Padel24Engine` object as a prop because it
 * reads runtime-mutable `.elo.*` / `.settings.*` and the `.getTournamentType` resolver.
 */
export function PlayerHistoryModal({
  playerId,
  effectivePlayerDB,
  archive,
  singleMatches = [],
  eloAdjustments = [],
  onClose,
  onOpenArchived,
  Padel24Engine,
  getPlayerTournamentHistory,
  calcPlayerPositionInTournament,
}: PlayerHistoryModalProps) {
  const [viewMode, setViewMode] = useState('matches'); // 'tournaments' | 'matches' — 'matches' is the default
  const [expandedTournament, setExpandedTournament] = useState(null); // which tournament is expanded in the Tournaments tab
  // `tr`, not `t`: in the tournament list below `t` is the tournament.
  const { t: tr, formatDate } = useLocale();
  const historyTitleId = safeId('player-history-title', useId());
  const player = effectivePlayerDB.find((p) => p.id === playerId);
  if (!player) return null;
  // The name as SHOWN — «Slettet spiller» for an erased player (#1062). Lookups
  // below keep `player.name`; this is display only.
  const shownName = playerDisplayName(player, tr('players.erased'));

  // deviation from byte-identical: the monolith calls these hooks after the early
  // `if (!player) return null` guard above; preserved verbatim (player is always
  // defined past the guard). eslint-disable for react-hooks/rules-of-hooks per the
  // sibling MatchTable convention. exhaustive-deps disabled where the monolith's
  // dep arrays intentionally omit stable function props / derived inputs.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const fullHistory = useMemo(
    () => getPlayerTournamentHistory(playerId, archive, player?.name),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [playerId, archive, player?.name],
  );

  // Actual tournaments (excluding the virtual "single_matches" collection).
  // Used for counting and rendering — single matches are handled separately.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const history = useMemo(
    () =>
      fullHistory.filter(
        (h) => !h.tournament?.isVirtual && h.tournament?.archiveId !== 'virtual_single_matches',
      ),
    [fullHistory],
  );

  // Filter single matches the player took part in
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const playerSingleMatches = useMemo(() => {
    return (singleMatches || []).filter(
      (m) =>
        m.team1?.p1Id === playerId ||
        m.team1?.p2Id === playerId ||
        m.team2?.p1Id === playerId ||
        m.team2?.p2Id === playerId,
    );
  }, [singleMatches, playerId]);

  // Counts for the tabs: total number of matches + number of merits (top 5).
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const matchCount = useMemo(
    () => history.reduce((s, h) => s + (h.matches?.length || 0), 0) + playerSingleMatches.length,
    [history, playerSingleMatches],
  );
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const meritCount = useMemo(() => {
    let n = 0;
    history.forEach((h) => {
      const r = calcPlayerPositionInTournament(playerId, h.tournament);
      if (r && r.position <= 5) n++;
    });
    return n;
    // deviation: the original dep array intentionally omits the stable
    // `calcPlayerPositionInTournament` function prop; preserved verbatim.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history, playerId]);

  // Manual ELO adjustments for this player
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const playerAdjustments = useMemo(() => {
    return (eloAdjustments || [])
      .filter((a) => a.playerId === playerId)
      .sort((a, b) => (b.adjustedAt || 0) - (a.adjustedAt || 0));
  }, [eloAdjustments, playerId]);

  // Aggregate stats across all tournaments (both formats via getTeamPlayerIds)
  // deviation: `_totalDraws` was dead in the original too (incremented, never read);
  // kept verbatim for byte-fidelity, `_`-prefixed instead of eslint-disabled (prettier-stable).
  let totalMatches = 0,
    totalWins = 0,
    totalLosses = 0,
    _totalDraws = 0;
  history.forEach((h) => {
    h.matches.forEach((m) => {
      if (!m.completed) return;
      const [t1p1, t1p2] = getTeamPlayerIds(m.team1);
      const t1Has = t1p1 === playerId || t1p2 === playerId;
      const t1Won = m.score1 > m.score2;
      const t2Won = m.score2 > m.score1;
      totalMatches++;
      if ((t1Has && t1Won) || (!t1Has && t2Won)) totalWins++;
      else if ((t1Has && t2Won) || (!t1Has && t1Won)) totalLosses++;
      else _totalDraws++;
    });
  });

  // Add single matches to the stats
  playerSingleMatches.forEach((m) => {
    const t1Has = m.team1.p1Id === playerId || m.team1.p2Id === playerId;
    const t1Won = m.result?.winner === 'team1';
    totalMatches++;
    if ((t1Has && t1Won) || (!t1Has && !t1Won)) totalWins++;
    else totalLosses++;
  });

  const currentYear = new Date().getFullYear();
  const lastYear = currentYear - 1;
  const yearStart = player.yearStartElo ?? player.originalElo ?? 0;
  const change = (player.elo || 0) - yearStart;

  // ELO development per match (this year) — tournament matches + single matches combined
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const eloHistory = useMemo(() => {
    const yearStartTs = new Date(currentYear, 0, 1).getTime();

    // Collect all completed matches with an ELO change
    const events = [];

    // Tournament matches from the archive (or the active tournament)
    history.forEach((h) => {
      // For active tournaments: use the last match's timestamp as the reference time.
      // For archived ones: use archivedAt.
      const archivedAt =
        h.tournament.archivedAt ||
        Math.max(0, ...(h.matches || []).map((m) => m.playedAt || m.completedAt || 0)) ||
        (h.tournament.isLive ? Date.now() : 0);
      if (archivedAt < yearStartTs) return; // this year only
      h.matches.forEach((m, idx) => {
        if (!m.completed) return;
        const [t1p1, t1p2] = getTeamPlayerIds(m.team1);
        const [t2p1, t2p2] = getTeamPlayerIds(m.team2);
        const inT1 = t1p1 === playerId || t1p2 === playerId;
        const inT2 = t2p1 === playerId || t2p2 === playerId;
        if (!inT1 && !inT2) return;

        const change = inT1 ? m.eloChange1 : m.eloChange2;
        if (change == null) return; // skip matches with no stored change

        events.push({
          timestamp: (m.playedAt || m.completedAt || archivedAt) + idx, // prefer the match's own time
          change,
          label: 'T',
          kind: 'tournament',
        });
      });
    });

    // Single matches
    (playerSingleMatches || []).forEach((m) => {
      if ((m.playedAt || 0) < yearStartTs) return;
      const inT1 = m.team1.p1Id === playerId || m.team1.p2Id === playerId;
      const change = inT1 ? m.result?.t1Change : m.result?.t2Change;
      if (change == null) return;

      events.push({
        timestamp: m.playedAt,
        change,
        label: 'E',
        kind: 'single',
      });
    });

    // Manual ELO adjustments (outside of matches)
    (playerAdjustments || []).forEach((a) => {
      if ((a.adjustedAt || 0) < yearStartTs) return;
      events.push({
        timestamp: a.adjustedAt,
        change: a.change || 0,
        label: 'J',
        kind: 'adjustment',
      });
    });

    if (events.length === 0) return [];

    // Sort chronologically
    events.sort((a, b) => a.timestamp - b.timestamp);

    // Build the series — start with yearStart, then accumulate each change
    const series = [{ label: `${lastYear}`, elo: yearStart, kind: 'start' }];
    let runningElo = yearStart;
    events.forEach((e, i) => {
      runningElo += e.change;
      series.push({
        label: `${e.label}${i + 1}`,
        elo: Math.round(runningElo),
        kind: e.kind,
      });
    });

    return series;
  }, [history, playerSingleMatches, playerAdjustments, playerId, yearStart, currentYear, lastYear]);

  // A `Dialog` (#900): named by the player's name, Tab kept inside, Escape
  // closes as the backdrop and «Tilbake» do, focus given back to the row or
  // name that opened it. It is the GLOBAL profile and opens over other screens
  // (and over the KM player modal's «Full profil»), which is why it sits at
  // 10001; mounted last, it is the innermost dialog and owns the keyboard.
  // The inline styles moved across unchanged.
  return (
    <Dialog
      labelledBy={historyTitleId}
      onClose={onClose}
      showClose={false}
      backdropClassName=""
      backdropStyle={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 10001,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        background: 'rgba(15, 22, 17, 0.55)',
      }}
      className=""
      style={{
        background: '#ffffff',
        maxWidth: 800,
        width: '100%',
        maxHeight: '90vh',
        borderRadius: 16,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 25px 60px -10px rgba(0,0,0,0.4)',
        border: '1px solid #e7eae3',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: 20,
          borderBottom: '1px solid #e7eae3',
          background: '#fafbf7',
          flexShrink: 0,
        }}
      >
        {/* Back row */}
        <button
          type="button"
          onClick={onClose}
          className="text-xs font-mono text-ink-3 hover:text-ink-2 mb-2 flex items-center gap-1"
        >
          <ArrowLeft size={12} /> {tr('players.history.back')}
        </button>
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="font-mono text-[10px] uppercase tracking-wider text-ink-3 mb-1">
              {tr('players.history.kicker')}
              {player.gender === 'F'
                ? tr('players.history.kickerFemale')
                : tr('players.history.kickerMale')}
              {!player.isSystem && (
                <span className="ml-2 px-1 py-0.5 rounded bg-lime-bg text-lime-deep font-bold">
                  {tr('players.history.newBadge')}
                </span>
              )}
            </div>
            <h2
              id={historyTitleId}
              className="font-display text-2xl font-semibold leading-tight mb-1"
            >
              {shownName}
            </h2>
            {player.email && (
              <a
                href={`mailto:${player.email}`}
                className="text-xs font-mono text-ink-3 hover:text-ink-2 hover:underline inline-flex items-center gap-1"
                onClick={(e) => e.stopPropagation()}
              >
                ✉ {player.email}
              </a>
            )}
            <div className="flex items-center gap-4 text-xs text-ink-3 font-mono mt-2 flex-wrap">
              <span title={tr('players.history.eloAtYearEndTitle', { year: String(lastYear) })}>
                {tr('players.history.eloYear', { year: String(lastYear) })}{' '}
                <span className="font-bold text-ink">{yearStart}</span>
              </span>
              <span>
                {tr('players.history.current')}{' '}
                <span className="font-bold text-ink">{player.elo}</span>
              </span>
              <span
                className={change > 0 ? 'text-lime-deep' : change < 0 ? 'text-rose' : 'text-ink-3'}
                title={tr('players.history.changeYearTitle', { year: String(currentYear) })}
              >
                {tr('players.history.changeYear', { year: String(currentYear) })}{' '}
                <span className="font-bold">
                  {change === 0 ? '–' : `${change > 0 ? '+' : ''}${change}`}
                </span>
              </span>
            </div>

            {/* Newbie / comeback status — shown only for players with a boost.
                  Full history is tournament-independent, so we use the default
                  kMultiplier (1.0). The per-tournament player profile uses the
                  tournament's own kMul to give an accurate "matches remaining". */}
            {(() => {
              const status = Padel24Engine.elo.getPlayerStatus(player);
              if (!status.isBoosted) return null;
              const isNew = status.isNew;
              return (
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  <span
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-mono font-bold"
                    style={{
                      background: isNew ? '#fef3c7' : '#dbeafe',
                      color: isNew ? '#92400e' : '#1e3a8a',
                      border: `1px solid ${isNew ? '#fde68a' : '#bfdbfe'}`,
                    }}
                    title={
                      isNew
                        ? tr('players.history.newPlayerTitle', {
                            name: shownName,
                            boost: String(Padel24Engine.settings.NEWBIE_K_MULTIPLIER),
                            count: Padel24Engine.elo.NEWBIE_MATCH_THRESHOLD,
                            doubled: Padel24Engine.elo.NEWBIE_MATCH_THRESHOLD * 2,
                          })
                        : tr('players.common.comebackTitle', {
                            days: Padel24Engine.elo.NEWBIE_INACTIVE_DAYS,
                            name: shownName,
                          })
                    }
                  >
                    {isNew ? '🔰' : '🔄'}{' '}
                    {isNew ? tr('players.common.statusNew') : tr('players.common.statusComeback')} ·{' '}
                    {Padel24Engine.elo.formatBoost(status.currentBoost)} ELO
                  </span>
                  {isNew && (
                    <span className="text-[11px] text-ink-3">
                      {status.matchesUntilEstablished > 0
                        ? tr(
                            status.matchesUntilEstablished === 1
                              ? 'players.common.matchesLeft.one'
                              : 'players.common.matchesLeft.other',
                            { count: status.matchesUntilEstablished },
                          )
                        : tr('players.common.nearlyEstablished')}
                    </span>
                  )}
                </div>
              );
            })()}
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              fontSize: 12,
              color: '#4a544c',
              padding: '4px 10px',
              borderRadius: 999,
            }}
            aria-label={tr('players.history.close')}
          >
            <X size={14} /> {tr('players.history.close')}
          </button>
        </div>
      </div>

      {/* Stats grid */}
      <div className="px-5 py-4 border-b border-line grid grid-cols-2 sm:grid-cols-4 gap-3 flex-shrink-0">
        <div className="text-center">
          <div className="font-display text-xl font-bold">{history.length}</div>
          <div className="font-mono text-[10px] uppercase tracking-wider text-ink-3 mt-0.5">
            {tr('players.history.tournaments')}
          </div>
        </div>
        <div className="text-center">
          <div className="font-display text-xl font-bold">{totalMatches}</div>
          <div className="font-mono text-[10px] uppercase tracking-wider text-ink-3 mt-0.5">
            {tr('players.history.totalMatches')}
          </div>
        </div>
        <div className="text-center">
          <div className="font-display text-xl font-bold text-lime-deep">{totalWins}</div>
          <div className="font-mono text-[10px] uppercase tracking-wider text-ink-3 mt-0.5">
            {tr('players.history.wins')}
          </div>
        </div>
        <div className="text-center">
          <div className="font-display text-xl font-bold text-rose">{totalLosses}</div>
          <div className="font-mono text-[10px] uppercase tracking-wider text-ink-3 mt-0.5">
            {tr('players.history.losses')}
          </div>
        </div>
      </div>

      {/* ELO progression graph — shown if the player has played in at least one tournament this year */}
      {eloHistory.length >= 2 && (
        <div className="px-5 py-4 border-b border-line flex-shrink-0">
          <div className="flex items-center justify-between mb-2">
            <div className="font-mono text-[11px] uppercase tracking-wider text-ink-3 font-semibold">
              {tr('players.history.eloDevelopmentYear', { year: String(currentYear) })}
            </div>
            <div className="text-xs font-mono">
              <span className="text-ink-3">{yearStart}</span>
              <span className="text-ink-4 mx-1">→</span>
              <span className="font-bold text-ink">{player.elo}</span>
            </div>
          </div>
          <EloChart data={eloHistory} height={140} />
        </div>
      )}

      {/* Tournament history / All matches */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        <div className="p-5">
          {/* Toggle */}
          <div className="flex items-center gap-1 border border-line rounded-full bg-paper p-0.5 text-xs mb-4 w-max">
            <button
              onClick={() => setViewMode('matches')}
              className={`px-3.5 py-1.5 rounded-full font-medium transition-colors ${
                viewMode === 'matches' ? 'bg-ink text-white' : 'text-ink-2 hover:bg-bg-soft'
              }`}
            >
              {tr('players.history.tabMatches', { count: matchCount })}
            </button>
            <button
              onClick={() => setViewMode('tournaments')}
              className={`px-3.5 py-1.5 rounded-full font-medium transition-colors ${
                viewMode === 'tournaments' ? 'bg-ink text-white' : 'text-ink-2 hover:bg-bg-soft'
              }`}
            >
              {tr('players.history.tabTournaments', { count: history.length })}
            </button>
            <button
              onClick={() => setViewMode('merits')}
              className={`px-3.5 py-1.5 rounded-full font-medium transition-colors ${
                viewMode === 'merits' ? 'bg-ink text-white' : 'text-ink-2 hover:bg-bg-soft'
              }`}
              title={tr('players.history.tabMeritsTitle')}
            >
              {tr('players.history.tabMerits', { count: meritCount })}
            </button>
          </div>

          {viewMode === 'merits' ? (
            <MeritsView
              playerId={playerId}
              history={history}
              onOpenArchived={onOpenArchived}
              calcPlayerPositionInTournament={calcPlayerPositionInTournament}
            />
          ) : viewMode === 'tournaments' ? (
            <>
              {history.length === 0 ? (
                <div className="card p-8 text-center text-sm text-ink-3 italic">
                  {tr('players.history.noTournaments', { name: shownName })}
                </div>
              ) : (
                <div className="space-y-2">
                  {history.map((h) => {
                    const t = h.tournament;
                    // Compute placement — use the stored h.finalPosition if available,
                    // otherwise compute it dynamically via the same logic as the Merits tab.
                    const positionResult =
                      h.finalPosition != null
                        ? { position: h.finalPosition }
                        : calcPlayerPositionInTournament(playerId, t);
                    // For active/paused: use the last match's timestamp, otherwise archivedAt
                    const dateMs =
                      t.archivedAt ||
                      Math.max(
                        0,
                        ...(h.matches || []).map((m) => m.playedAt || m.completedAt || 0),
                      );
                    const date = dateMs > 0 ? new Date(dateMs) : null;
                    const dateLabel = date
                      ? formatDate(date, {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric',
                        })
                      : tr('players.history.unknownDate');

                    // ELO at the tournament: use startElo if stored (Padel24), otherwise
                    // the first match's playerEloPre (Americano stores pre-match ELO per player per match),
                    // otherwise fall back to the current value (best possible estimate)
                    const firstMatchWithPre = h.matches
                      .filter((m) => m.completed)
                      .sort(
                        (a, b) =>
                          (a.playedAt || a.completedAt || 0) - (b.playedAt || b.completedAt || 0),
                      )
                      .find((m) => m.playerEloPre?.[playerId] != null);
                    const isTeamFmt = t.type === 'klubbmesterskap' || t.type === 'seriespill';
                    let eloBefore, eloAfter, eloDiff;
                    if (isTeamFmt) {
                      // Team-based formats: performance = sum of the team's ELO change in
                      // the player's matches; "before" = the team's average ELO in the first match.
                      const cs = h.matches
                        .filter((m) => m.completed)
                        .sort(
                          (a, b) =>
                            (a.playedAt || a.completedAt || 0) -
                              (b.playedAt || b.completedAt || 0) || (a.round || 0) - (b.round || 0),
                        );
                      let delta = 0,
                        firstPre = null;
                      cs.forEach((m) => {
                        const inT1 = m.team1?.p1Id === playerId || m.team1?.p2Id === playerId;
                        const ch = inT1 ? m.eloChange1 : m.eloChange2;
                        if (typeof ch === 'number') delta += ch;
                        if (firstPre == null) {
                          const pre = inT1 ? m.teamElo1Pre : m.teamElo2Pre;
                          if (pre != null) firstPre = pre;
                        }
                      });
                      eloDiff = delta;
                      eloBefore = firstPre;
                      eloAfter = firstPre != null ? firstPre + delta : null;
                    } else {
                      eloBefore =
                        h.playerInTournament.startElo ??
                        firstMatchWithPre?.playerEloPre?.[playerId] ??
                        h.playerInTournament.elo;
                      eloAfter = h.playerInTournament.elo;
                      eloDiff = eloAfter - eloBefore;
                    }
                    if (!Number.isFinite(eloDiff)) eloDiff = 0;

                    // Stats
                    let w = 0,
                      l = 0,
                      d = 0;
                    h.matches.forEach((m) => {
                      if (!m.completed) return;
                      const [t1p1, t1p2] = getTeamPlayerIds(m.team1);
                      const t1Has = t1p1 === playerId || t1p2 === playerId;
                      const t1Won = m.score1 > m.score2;
                      const t2Won = m.score2 > m.score1;
                      if ((t1Has && t1Won) || (!t1Has && t2Won)) w++;
                      else if ((t1Has && t2Won) || (!t1Has && t1Won)) l++;
                      else d++;
                    });

                    const isExpanded = expandedTournament === h.key;
                    const sortedMatches = [...h.matches].sort((a, b) => {
                      const tb =
                        (b.playedAt || b.completedAt || 0) - (a.playedAt || a.completedAt || 0);
                      if (tb !== 0) return tb;
                      const ra = typeof a.round === 'number' ? a.round : 0;
                      const rb = typeof b.round === 'number' ? b.round : 0;
                      return rb - ra;
                    });
                    return (
                      <div key={h.key} className="card p-3">
                        {/* THE CHEVRON IS THE KEYBOARD WAY TO EXPAND (#900). The row
                            opened its matches on a click, but it was a <div>, so a
                            keyboard could not see them at all. The chevron is a
                            real button with `aria-expanded`; the row keeps its
                            click as a pointer convenience that only repeats it,
                            so it is role="presentation". */}
                        <div
                          role="presentation"
                          className="flex items-center gap-3 cursor-pointer"
                          onClick={() => setExpandedTournament(isExpanded ? null : h.key)}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              {(() => {
                                const genColor = (g) =>
                                  g === 'F'
                                    ? '#c01074'
                                    : g === 'M'
                                      ? '#1961d3'
                                      : g === 'X'
                                        ? '#7845df'
                                        : null;
                                const pg = player?.gender;
                                const tt =
                                  t.type === 'klubbmesterskap'
                                    ? {
                                        abbrev: 'KM',
                                        color: genColor(pg) || '#f59e0b',
                                        label: 'Klubbmesterskap',
                                      }
                                    : t.type === 'seriespill'
                                      ? {
                                          abbrev: 'Serie',
                                          color: genColor(pg) || '#1961d3',
                                          label: 'Seriespill',
                                        }
                                      : Padel24Engine.getTournamentType(t.config);
                                return (
                                  <span
                                    className="font-mono text-[10px] font-bold px-1.5 py-0.5 rounded"
                                    style={{ background: tt.color + '40', color: '#0f1611' }}
                                    title={tt.label}
                                  >
                                    {tt.abbrev}
                                  </span>
                                );
                              })()}
                              <span className="font-mono text-[10px] text-ink-3">{dateLabel}</span>
                              {positionResult?.position != null && (
                                <span
                                  className={`font-mono text-[10px] font-bold px-1.5 py-0.5 rounded flex items-center gap-1 ${
                                    positionResult.position === 1
                                      ? 'bg-lime-bg text-lime-deep'
                                      : positionResult.position <= 3
                                        ? 'bg-amber-100 text-amber-800'
                                        : positionResult.position <= 5
                                          ? 'bg-bg-soft text-ink-2'
                                          : 'bg-bg-soft/60 text-ink-3'
                                  }`}
                                >
                                  {positionResult.position === 1
                                    ? '🥇'
                                    : positionResult.position === 2
                                      ? '🥈'
                                      : positionResult.position === 3
                                        ? '🥉'
                                        : positionResult.position <= 5
                                          ? '🏆'
                                          : null}
                                  {tr('players.history.placement', {
                                    position: positionResult.position,
                                  })}
                                </span>
                              )}
                            </div>
                            <div className="font-semibold text-sm leading-tight truncate">
                              {t.config?.name || tr('players.history.tournamentFallback')}
                            </div>
                            <div className="text-xs text-ink-3 mt-0.5">
                              {tr('players.history.matchSummary', {
                                matches: h.matches.length,
                                wins: w,
                                losses: l,
                              })}
                              {d > 0 ? tr('players.history.drawsSuffix', { draws: d }) : ''}
                            </div>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <div className="font-mono text-[10px] text-ink-3">
                              {Number.isFinite(eloBefore) && Number.isFinite(eloAfter)
                                ? `${Math.round(eloBefore)} → ${Math.round(eloAfter)}`
                                : tr('players.history.eloInTournament')}
                            </div>
                            <div
                              className={`font-mono text-sm font-bold ${
                                eloDiff > 0
                                  ? 'text-lime-deep'
                                  : eloDiff < 0
                                    ? 'text-rose'
                                    : 'text-ink-3'
                              }`}
                            >
                              {eloDiff === 0 ? '±0' : `${eloDiff > 0 ? '+' : ''}${eloDiff}`}
                            </div>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onOpenArchived(t);
                              onClose();
                            }}
                            title={tr('players.history.goToTournament')}
                            aria-label={tr('players.history.goToTournament')}
                            className="flex-shrink-0 p-1.5 rounded-full border border-line text-ink-3 hover:text-ink hover:border-ink-3 transition-colors"
                          >
                            <ArrowRight size={14} />
                          </button>
                          <button
                            type="button"
                            aria-expanded={isExpanded}
                            aria-label={tr('players.history.showMatches', {
                              name: t.config?.name || tr('players.history.tournamentFallback'),
                            })}
                            onClick={(e) => {
                              e.stopPropagation();
                              setExpandedTournament(isExpanded ? null : h.key);
                            }}
                            className="flex-shrink-0 flex items-center justify-center w-6 h-6 rounded text-ink-3 focus-ring"
                          >
                            <ChevronDown
                              size={16}
                              className={`transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                            />
                          </button>
                        </div>
                        {isExpanded && (
                          <div className="mt-2 pt-2 border-t border-line/50 space-y-1">
                            {sortedMatches.length === 0 ? (
                              <div className="text-xs text-ink-3 italic py-2 text-center">
                                {tr('players.history.noMatches')}
                              </div>
                            ) : (
                              sortedMatches.map((m, mi) => (
                                <PlayerMatchRow
                                  key={mi}
                                  match={m}
                                  playerId={playerId}
                                  players={t.players || []}
                                  kmPairs={
                                    t.type === 'klubbmesterskap' || t.type === 'seriespill'
                                      ? t.pairs || []
                                      : null
                                  }
                                />
                              ))
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          ) : (
            /* The all-matches view */
            <AllMatchesView
              playerId={playerId}
              history={history}
              singleMatches={playerSingleMatches}
              adjustments={playerAdjustments}
              allPlayers={effectivePlayerDB}
            />
          )}
        </div>
      </div>
    </Dialog>
  );
}
